import { useState, useMemo } from 'react';
import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Briefcase, ChevronDown, ChevronRight } from 'lucide-react';

interface Project {
  id: string;
  name: string;
  company: string | null;
  product_1: string | null;
  product_alloc_1: number | null;
  product_2: string | null;
  product_alloc_2: number | null;
  function: string | null;
  opex_capex: string | null;
  category: string | null;
  legal_entity: string | null;
  notes: string | null;
  is_active: boolean;
  jan: number | null; feb: number | null; mar: number | null;
  apr: number | null; may: number | null; jun: number | null;
  jul: number | null; aug: number | null; sep: number | null;
  oct: number | null; nov: number | null; dec_val: number | null;
  total: number | null;
  created_at: string;
}

const MONTHS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec_val'] as const;
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// fmt provided by useCurrency — initialized inside component

function getTotal(p: Project): number {
  if (p.total != null) return p.total;
  return MONTHS.reduce((s, m) => s + (p[m] || 0), 0);
}

export default function Projects() {
  const { fmt: cfmt } = useCurrency();
  const fmt = (n: number | null) => {
    if (n == null || n === 0) return '—';
    return cfmt(n, 'EUR');
  };
  const { data, loading, error } = useFetch<Project[]>('/api/fin/projects');
  const [companyFilter, setCompanyFilter] = useState('__all__');
  const [typeFilter, setTypeFilter] = useState('__all__');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const { filtered, companies, summaryCards } = useMemo(() => {
    if (!data) return { filtered: [], companies: [], summaryCards: [] };

    // Filter out empty "Unknown" projects
    const real = data.filter(p => p.name && p.name !== 'Unknown');
    const allCompanies = [...new Set(real.map(p => p.company).filter(Boolean))] as string[];

    let items = real;
    if (companyFilter !== '__all__') items = items.filter(p => p.company === companyFilter);
    if (typeFilter !== '__all__') items = items.filter(p => p.opex_capex?.toLowerCase() === typeFilter.toLowerCase());
    if (statusFilter !== '__all__') items = items.filter(p => p.is_active === (statusFilter === 'active'));

    const totalOpex = items.filter(p => p.opex_capex?.toLowerCase() === 'opex').reduce((s, p) => s + Math.abs(getTotal(p)), 0);
    const totalCapex = items.filter(p => p.opex_capex?.toLowerCase() === 'capex').reduce((s, p) => s + Math.abs(getTotal(p)), 0);

    return {
      filtered: items,
      companies: allCompanies,
      summaryCards: [
        { label: 'Total Opex', value: fmt(-totalOpex), color: 'text-blue-600' },
        { label: 'Total Capex', value: fmt(-totalCapex), color: 'text-purple-600' },
        { label: 'Active Projects', value: String(items.filter(p => p.is_active).length), color: 'text-emerald-600' },
        { label: 'Total Projects', value: String(items.length), color: 'text-foreground' },
      ],
    };
  }, [data, companyFilter, typeFilter, statusFilter]);

  if (loading && !data) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-4 md:p-6">
        <h1 className="text-2xl font-semibold mb-2">Projects</h1>
        <Card><CardContent className="py-12 text-center text-muted-foreground">{error}</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground tracking-tight flex items-center gap-2">
          <Briefcase size={24} /> Projects
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Project budget tracker with monthly breakdown</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {summaryCards.map(card => (
          <Card key={card.label} className="shadow-xs">
            <CardContent className="p-5">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{card.label}</div>
              <div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={companyFilter} onValueChange={setCompanyFilter}>
          <SelectTrigger><SelectValue placeholder="Company" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Companies</SelectItem>
            {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Types</SelectItem>
            <SelectItem value="opex">Opex</SelectItem>
            <SelectItem value="capex">Capex</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Projects table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead className="text-xs">Project</TableHead>
                <TableHead className="text-xs">Company</TableHead>
                <TableHead className="text-xs">Product</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Category</TableHead>
                <TableHead className="text-xs">Entity</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No projects found</TableCell>
                </TableRow>
              )}
              {filtered.map(p => {
                const total = getTotal(p);
                const isExpanded = expandedRows.has(p.id);
                return (
                  <>
                    <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => toggleRow(p.id)}>
                      <TableCell className="w-8">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-sm truncate max-w-[250px]">{p.name}</div>
                        {p.notes && <div className="text-xs text-muted-foreground truncate max-w-[250px]">{p.notes}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{p.company || '—'}</TableCell>
                      <TableCell className="text-sm">{p.product_1 || '—'}</TableCell>
                      <TableCell>
                        {p.opex_capex && (
                          <Badge variant="outline" className={`text-xs ${p.opex_capex.toLowerCase() === 'capex' ? 'text-purple-600 border-purple-300' : 'text-blue-600 border-blue-300'}`}>
                            {p.opex_capex}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.category || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.legal_entity || '—'}</TableCell>
                      <TableCell>
                        <Badge variant={p.is_active ? 'default' : 'secondary'} className="text-xs">
                          {p.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-sm font-medium text-right tabular-nums ${total < 0 ? 'text-red-600' : 'text-foreground'}`}>
                        {fmt(total)}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow key={`${p.id}-detail`}>
                        <TableCell colSpan={9} className="bg-muted/30 p-4">
                          <div className="grid grid-cols-6 sm:grid-cols-12 gap-2 text-xs">
                            {MONTHS.map((m, i) => (
                              <div key={m} className="text-center">
                                <div className="font-medium text-muted-foreground mb-1">{MONTH_LABELS[i]}</div>
                                <div className={`tabular-nums ${(p[m] || 0) < 0 ? 'text-red-600' : (p[m] || 0) > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                                  {p[m] != null && p[m] !== 0 ? fmt(p[m]) : '—'}
                                </div>
                              </div>
                            ))}
                          </div>
                          {p.product_2 && (
                            <div className="mt-3 text-xs text-muted-foreground">
                              Secondary product: {p.product_2} ({((p.product_alloc_2 || 0) * 100).toFixed(0)}%)
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
